import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { APP_PATH } from "../api/backendPath";

/**
 * useChatUnread - fetches the provider's chat rooms once and exposes per-appointment
 * (and per-patient) unread counts so list screens can show a badge on the chat icon.
 *
 * Returns:
 *  - unreadForAppointment(bookingId): number   -> unread count for that appointment room
 *  - unreadForPatient(patientUserId): number   -> unread for a general (per-patient) room
 *  - refresh(): re-fetch the rooms
 *  - clearAppointment(bookingId): optimistically zero a badge (e.g. after opening the chat)
 */
export default function useChatUnread() {
  const [byAppointment, setByAppointment] = useState({});
  const [byPatient, setByPatient] = useState({});

  const refresh = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      if (!token) return;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}${APP_PATH.consultationChatRooms}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rooms = res?.data?.data?.rooms || res?.data?.rooms || [];
      const apptMap = {};
      const patientMap = {};
      rooms.forEach((r) => {
        const unread = Number(r.unread_count || 0);
        if (r.appointment_id != null) {
          apptMap[r.appointment_id] = (apptMap[r.appointment_id] || 0) + unread;
        }
        (r.other_user_ids || []).forEach((uid) => {
          patientMap[uid] = (patientMap[uid] || 0) + unread;
        });
      });
      setByAppointment(apptMap);
      setByPatient(patientMap);
    } catch (e) {
      // silent — badges are non-critical
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live-refresh when a new chat message arrives (dispatched by useSocketNotifications'
  // global `new_message` handler) so the unread badge updates without a manual reload.
  useEffect(() => {
    const onRefresh = () => refresh();
    window.addEventListener("chat-unread-refresh", onRefresh);
    return () => window.removeEventListener("chat-unread-refresh", onRefresh);
  }, [refresh]);

  const unreadForAppointment = useCallback(
    (bookingId) => (bookingId != null ? Number(byAppointment[bookingId] || 0) : 0),
    [byAppointment]
  );

  const unreadForPatient = useCallback(
    (patientUserId) => (patientUserId != null ? Number(byPatient[patientUserId] || 0) : 0),
    [byPatient]
  );

  const clearAppointment = useCallback((bookingId) => {
    if (bookingId == null) return;
    setByAppointment((prev) => ({ ...prev, [bookingId]: 0 }));
  }, []);

  return { unreadForAppointment, unreadForPatient, refresh, clearAppointment };
}
